// Type-and-pick combobox: a normal text input the user can type freely into,
// with a live dropdown of matching suggestions they can also click. Built for
// non-technical users — you can always just type a name, or open the list and
// pick one. Free-typed values that aren't in the list are accepted as-is.
//
// The dropdown renders through a portal at a high z-index and is positioned
// from the input's bounding rect, so it works correctly inside modals and
// scroll containers without being clipped.

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check } from 'lucide-react'

export interface ComboboxOption {
  value: string
  // Optional secondary text shown dimmed next to the value (e.g. a column's
  // data type or a table's description).
  hint?: string
}

interface Props {
  value: string
  onChange: (next: string) => void
  options: ComboboxOption[]
  placeholder?: string
  // Visual error state (red border).
  invalid?: boolean
  // Shown in the dropdown when there are no options at all.
  emptyText?: string
  // Shown when the typed query matches no option (custom value still allowed).
  noMatchText?: string
  loading?: boolean
  disabled?: boolean
  ariaLabel?: string
  inputId?: string
  style?: React.CSSProperties
}

const DROPDOWN_MAX = 280

const inputBase: React.CSSProperties = {
  width: '100%',
  padding: '6px 30px 6px 10px',
  background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 4,
  color: 'var(--color-text)',
  fontSize: '0.9125rem',
  boxSizing: 'border-box',
  outline: 'none',
}

export default function Combobox({
  value, onChange, options, placeholder, invalid, emptyText, noMatchText,
  loading, disabled, ariaLabel, inputId, style,
}: Props) {
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()

  // Filter options by the current input text (substring on value + hint).
  const filtered = useMemo(() => {
    const q = (value || '').trim().toLowerCase()
    if (!q) return options
    return options.filter(o =>
      o.value.toLowerCase().includes(q)
      || (o.hint || '').toLowerCase().includes(q),
    )
  }, [value, options])

  const exactMatch = useMemo(
    () => options.some(o => o.value.toLowerCase() === (value || '').trim().toLowerCase()),
    [options, value],
  )

  // ── Position the dropdown from the input rect (portal → body). ──
  const [pos, setPos] = useState<{ top: number; left: number; width: number; above: boolean } | null>(null)
  useLayoutEffect(() => {
    if (!open) return
    const update = () => {
      const el = wrapRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - r.bottom
      const above = spaceBelow < DROPDOWN_MAX + 16 && r.top > spaceBelow
      setPos({
        top: above ? r.top : r.bottom + 4,
        left: r.left,
        width: r.width,
        above,
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapRef.current?.contains(t)) return
      if (listRef.current?.contains(t)) return
      setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])

  // Clamp highlight when the filtered set changes.
  useEffect(() => {
    setHighlight(h => Math.min(Math.max(0, h), Math.max(0, filtered.length - 1)))
  }, [filtered.length])

  const choose = (v: string) => {
    onChange(v)
    setOpen(false)
    inputRef.current?.focus()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) { setOpen(true); return }
      setHighlight(h => Math.min(filtered.length - 1, h + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(0, h - 1))
    } else if (e.key === 'Enter') {
      if (open && filtered[highlight]) {
        e.preventDefault()
        choose(filtered[highlight].value)
      } else {
        setOpen(false)
      }
    } else if (e.key === 'Escape') {
      if (open) { e.stopPropagation(); setOpen(false) }
    }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...style }}>
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        onChange={e => { onChange(e.target.value); if (!open) setOpen(true) }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
        style={{
          ...inputBase,
          border: invalid ? '1px solid #ef4444' : inputBase.border,
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Toggle list"
        disabled={disabled}
        onClick={() => { setOpen(o => !o); inputRef.current?.focus() }}
        style={{
          position: 'absolute', right: 2, top: 0, bottom: 0,
          width: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent', border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: 'var(--color-text-secondary)',
        }}
      >
        <ChevronDown
          size={15}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.12s' }}
        />
      </button>

      {open && pos && createPortal(
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          style={{
            position: 'fixed',
            left: pos.left,
            width: pos.width,
            ...(pos.above
              ? { bottom: window.innerHeight - pos.top + 4 }
              : { top: pos.top }),
            maxHeight: DROPDOWN_MAX,
            overflowY: 'auto',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            boxShadow: '0 12px 32px rgba(0,0,0,0.30)',
            zIndex: 10000,
          }}
        >
          {loading ? (
            <div style={emptyRow}>Loading…</div>
          ) : options.length === 0 ? (
            <div style={emptyRow}>{emptyText || 'No suggestions — type a name.'}</div>
          ) : filtered.length === 0 ? (
            <div style={emptyRow}>
              {noMatchText || `No match — “${value.trim()}” will be used as typed.`}
            </div>
          ) : (
            filtered.map((o, i) => {
              const active = i === highlight
              const selected = o.value.toLowerCase() === (value || '').trim().toLowerCase()
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setHighlight(i)}
                  // mousedown (not click) so the input's blur doesn't close
                  // the list before the selection registers.
                  onMouseDown={(e) => { e.preventDefault(); choose(o.value) }}
                  style={{
                    width: '100%', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px', border: 'none',
                    background: active ? 'var(--color-accent-weak)' : 'transparent',
                    color: 'var(--color-text)', cursor: 'pointer',
                  }}
                >
                  <span style={{
                    flexShrink: 0, width: 14,
                    color: 'var(--color-accent)',
                    display: 'inline-flex', alignItems: 'center',
                  }}>
                    {selected ? <Check size={14} /> : null}
                  </span>
                  <span style={{
                    fontSize: '0.8925rem', fontFamily: 'monospace',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{o.value}</span>
                  {o.hint && (
                    <span style={{
                      marginLeft: 'auto', fontSize: '0.8125rem',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      maxWidth: '45%',
                    }}>{o.hint}</span>
                  )}
                </button>
              )
            })
          )}
          {/* When the user has typed a value that isn't an exact option, make
              it explicit that the custom text is what will be saved. */}
          {!loading && value.trim() && !exactMatch && filtered.length > 0 && (
            <div style={{
              padding: '6px 10px', borderTop: '1px solid var(--color-border)',
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
            }}>
              Press Enter to keep “{value.trim()}” as typed.
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}

const emptyRow: React.CSSProperties = {
  padding: '12px 12px',
  fontSize: '0.8425rem',
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
}
